/**
 * Employee Compliance Tab Component
 * Führungszeugnis & Masernschutz
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { FileCheck, Syringe, AlertCircle } from 'lucide-react';

const ComplianceTab = ({ 
  formData, 
  handlePoliceCheckChange, 
  handleMeaslesChange,
  isMeaslesRequired 
}) => {
  return (
    <div className="space-y-6">
      {/* Führungszeugnis */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-primary" />
            Erweitertes Führungszeugnis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="police_issue_date">Ausstellungsdatum</Label>
              <DatePickerDE
                value={formData.police_check?.issue_date || ''}
                onChange={(v) => handlePoliceCheckChange('issue_date', v)}
                placeholder="Ausstellungsdatum"
                testId="police-issue-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="police_review_date">Datum der Einsichtnahme</Label>
              <DatePickerDE
                value={formData.police_check?.review_date || ''}
                onChange={(v) => handlePoliceCheckChange('review_date', v)}
                placeholder="Einsichtsdatum"
                testId="police-review-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="police_reviewed_by">Einsicht genommen von</Label>
              <Input
                id="police_reviewed_by"
                value={formData.police_check?.reviewed_by || ''}
                onChange={(e) => handlePoliceCheckChange('reviewed_by', e.target.value)}
                placeholder="Name des Prüfers"
                data-testid="police-reviewed-by"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Checkbox
                id="police_valid"
                checked={formData.police_check?.valid || false}
                onCheckedChange={(v) => handlePoliceCheckChange('valid', v)}
                data-testid="police-valid"
              />
              <Label htmlFor="police_valid">Führungszeugnis gültig / ohne Eintrag</Label>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="police_notes">Notizen</Label>
            <Textarea
              id="police_notes"
              value={formData.police_check?.notes || ''}
              onChange={(e) => handlePoliceCheckChange('notes', e.target.value)}
              placeholder="Anmerkungen zum Führungszeugnis..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Masernschutz */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Syringe className="h-5 w-5 text-accent" />
            Masernschutz
            {isMeaslesRequired() && (
              <Badge className="badge-warning ml-2">
                <AlertCircle className="h-3 w-3 mr-1" />
                Pflicht (geb. nach 1970)
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!formData.date_of_birth ? (
            <p className="text-muted-foreground text-sm">
              Bitte zuerst das Geburtsdatum eingeben, um die Masernschutz-Pflicht zu prüfen.
            </p>
          ) : !isMeaslesRequired() ? (
            <p className="text-muted-foreground text-sm">
              Kein Masernschutz-Nachweis erforderlich (geboren vor 1971).
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="measles_proof_date">Nachweisdatum</Label>
                  <DatePickerDE
                    value={formData.measles_protection?.proof_date || ''}
                    onChange={(v) => handleMeaslesChange('proof_date', v)}
                    placeholder="Nachweisdatum"
                    testId="measles-proof-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="measles_proof_type">Art des Nachweises</Label>
                  <Select 
                    value={formData.measles_protection?.proof_type || ''} 
                    onValueChange={(v) => handleMeaslesChange('proof_type', v)}
                  >
                    <SelectTrigger data-testid="measles-proof-type">
                      <SelectValue placeholder="Auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Impfpass">Impfpass</SelectItem>
                      <SelectItem value="Titer-Nachweis">Titer-Nachweis (Bluttest)</SelectItem>
                      <SelectItem value="Ärztliches Attest">Ärztliches Attest</SelectItem>
                      <SelectItem value="Immunität">Immunität (frühere Erkrankung)</SelectItem>
                      <SelectItem value="Kontraindikation">Medizinische Kontraindikation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="measles_verified_by">Geprüft von</Label>
                  <Input
                    id="measles_verified_by"
                    value={formData.measles_protection?.verified_by || ''}
                    onChange={(e) => handleMeaslesChange('verified_by', e.target.value)}
                    placeholder="Name des Prüfers"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Checkbox
                    id="measles_verified"
                    checked={formData.measles_protection?.verified || false}
                    onCheckedChange={(v) => handleMeaslesChange('verified', v)}
                    data-testid="measles-verified"
                  />
                  <Label htmlFor="measles_verified">Masernschutz verifiziert</Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="measles_notes">Notizen</Label>
                <Textarea
                  id="measles_notes"
                  value={formData.measles_protection?.notes || ''}
                  onChange={(e) => handleMeaslesChange('notes', e.target.value)}
                  placeholder="Anmerkungen zum Masernschutz..."
                  rows={2}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceTab;
